import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import '../styles/global.css';

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', confirm_password: '',
    hotel_name: '', city: '', state: '', country: 'US', total_rooms: '', timezone: 'America/New_York'
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleStep1 = (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.hotel_name.trim()) { setError('Hotel name is required'); return; }
    setLoading(true);
    try {
      // Register user
      const authData = await api.post('/auth/register', {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        role: 'hotel_admin'
      });

      localStorage.setItem('accessToken', authData.accessToken);
      localStorage.setItem('refreshToken', authData.refreshToken);

      // Create hotel
      await api.post('/hotels', {
        name: form.hotel_name,
        city: form.city,
        state: form.state,
        country: form.country,
        total_rooms: form.total_rooms ? parseInt(form.total_rooms) : null,
        timezone: form.timezone
      });

      navigate('/manager');
    } catch (err) {
      setError(err.message);
      // If hotel creation failed but user was created, still redirect
      if (err.message.includes('hotel') || err.message.includes('Hotel')) {
        navigate('/manager');
      }
    }
    setLoading(false);
  };

  const timezones = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
    'America/Mexico_City', 'America/Cancun', 'America/Toronto',
    'America/Vancouver', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'
  ];

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 460 }}>
        <h1 className="login-title">Get Started</h1>
        <p className="login-subtitle">
          {step === 1 ? 'Create your account' : 'Set up your property'}
        </p>

        {/* Step indicator */}
        <div className="flex gap-2 mb-4" style={{ justifyContent: 'center' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--primary)' }} />
          <div style={{ width: 40, height: 4, borderRadius: 2, background: step >= 2 ? 'var(--primary)' : 'var(--border)' }} />
        </div>

        {error && <div className="login-error">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleStep1}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" value={form.first_name} onChange={e => set('first_name', e.target.value)} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" value={form.last_name} onChange={e => set('last_name', e.target.value)} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} required minLength={8} />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm</label>
                <input className="form-input" type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} required />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>Next</button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Hotel / Property Name</label>
              <input className="form-input" value={form.hotel_name} onChange={e => set('hotel_name', e.target.value)} required autoFocus placeholder="e.g. The Grand Hotel" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">State / Province</label>
                <input className="form-input" value={form.state} onChange={e => set('state', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Total Rooms</label>
                <input className="form-input" type="number" value={form.total_rooms} onChange={e => set('total_rooms', e.target.value)} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label className="form-label">Timezone</label>
                <select className="form-select" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                  {timezones.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-secondary btn-lg" style={{ flex: 1 }} onClick={() => setStep(1)}>Back</button>
              <button type="submit" className="btn btn-primary btn-lg" style={{ flex: 2 }} disabled={loading}>
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </div>
            <div className="form-hint mt-3" style={{ textAlign: 'center' }}>$79/mo per property. 14-day money-back guarantee.</div>
          </form>
        )}

        <div className="login-toggle">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
